/**
 * What a stored opt-out does before and outside React. The GTM container is
 * injected at the edge and never gated, and consent mode only reaches Google's
 * tags: the custom HTML tags (Microsoft UET, call tracking, the GA4 forwarder
 * that hard-codes gcs=G111 and carries the lead's email and phone) ignore it.
 * So the head snippet sets the consent default and also blocks those tag types
 * with gtm.blocklist, and it must run before the container's start event.
 */
export const CONSENT_COOKIE_KEY = process.env.NEXT_PUBLIC_COOKIE_CONSENT_KEY ?? 'cookie-consent'

export interface StoredConsent {
  analytics?: boolean
  advertising?: boolean
}

export function readStoredConsent(key: string = CONSENT_COOKIE_KEY): StoredConsent | null {
  if (typeof document === 'undefined') return null
  const prefix = `${key}=`
  const raw = document.cookie
    .split('; ')
    .find(cookie => cookie.startsWith(prefix))
    ?.slice(prefix.length)
  if (!raw) return null
  try {
    return JSON.parse(decodeURIComponent(raw)).settings ?? null
  } catch {
    return null
  }
}

/** A visitor who never chose is on implied consent, so only a stored refusal counts. */
export function hasOptedOut(key: string = CONSENT_COOKIE_KEY): boolean {
  const stored = readStoredConsent(key)
  if (!stored) return false
  return stored.analytics === false || stored.advertising === false
}

/**
 * Inline head script. Analytics off is a full opt-out, matching useConsent;
 * advertising off alone leaves the Google tag running under denied ad consent.
 */
export function consentHeadSnippet(key: string = CONSENT_COOKIE_KEY): string {
  return `(function(w,d){try{var p=${JSON.stringify(`${key}=`)},c=d.cookie.split('; ').filter(function(x){return x.indexOf(p)===0})[0];if(!c)return;var s=JSON.parse(decodeURIComponent(c.slice(p.length))).settings;if(!s||(s.analytics!==false&&s.advertising!==false))return;var a=s.analytics!==false,l=['html','awct','gclidw'];if(!a)l.push('googtag');w.dataLayer=w.dataLayer||[];(function(){w.dataLayer.push(arguments)})('consent','default',{analytics_storage:a?'granted':'denied',ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied'});w.dataLayer.push({'gtm.blocklist':l})}catch(e){}})(window,document)`
}
